import type { ScaleLinear } from 'd3-scale';

import { chartConfig } from '@config/chart';
import type { AggregatedPosition } from '@hooks/usePositionAggregation';
import { brandColors } from '@utils/color-utils';
import { formatPrice } from '@utils/price-formatting';

interface PositionMarkersProps {
  aggregatedPositions: AggregatedPosition[];
  yScale: ScaleLinear<number, number>;
  width: number;
  margin: { top: number; right: number; bottom: number; left: number };
  selectedPositionGroup?: AggregatedPosition | null;
  onPositionSelect?: (position: AggregatedPosition | null) => void;
}

export function PositionMarkers({
  aggregatedPositions,
  yScale,
  width,
  margin,
  selectedPositionGroup,
}: PositionMarkersProps) {
  if (aggregatedPositions.length === 0) {
    return null;
  }

  const chartWidth = width - margin.left - margin.right;
  const labelWidth = chartConfig.positionMarkers.labelWidth;

  return (
    <g>
      {aggregatedPositions.map((position, index) => {
        const y = yScale(position.breakEvenPrice);
        const isSelected = selectedPositionGroup?.id === position.id;
        const pnlColor =
          position.totalPnL >= 0 ? brandColors.green : brandColors.red;
        const displayColor = pnlColor; // Always use PnL color
        const strokeWidth = isSelected
          ? chartConfig.positionMarkers.strokeWidth.selected
          : chartConfig.positionMarkers.strokeWidth.normal;
        const opacity = isSelected
          ? chartConfig.positionMarkers.opacity.selected
          : chartConfig.positionMarkers.opacity.normal;
        const directionArrow = position.dominantType === 'CALL' ? '↑' : '↓';

        // Breakeven line across full chart with interactive area
        return (
          <g key={`${position.dominantType}-${index}`}>
            {/* Breakeven line - stops at label */}
            <line
              x1={0}
              x2={chartWidth - labelWidth}
              y1={y}
              y2={y}
              stroke={displayColor}
              strokeWidth={strokeWidth}
              opacity={opacity}
              style={{ pointerEvents: 'none' }}
            />

            {/* Tooltip area (non-interactive, just for hover info) */}
            <rect
              x={0}
              y={y - 8}
              width={chartWidth}
              height={16}
              fill="transparent"
              style={{ pointerEvents: 'none' }}
            >
              <title>
                {`${position.dominantType} Positions (${position.dominantType === 'CALL' ? position.callCount : position.putCount})\n` +
                  `Breakeven: $${position.breakEvenPrice.toFixed(2)}\n` +
                  `Total Quantity: ${position.totalQuantity}\n` +
                  `Current PnL: ${position.totalPnL >= 0 ? '+' : ''}$${position.totalPnL.toFixed(2)}\n\n` +
                  `Click to ${isSelected ? 'deselect' : 'select and close'}`}
              </title>
            </rect>

            {/* Right side PnL label extending to edge - rendered after line for z-order */}
            <g transform={`translate(${chartWidth - labelWidth}, ${y})`}>
              {/* Label background - filled shape */}
              <path
                d={`M 4 -12 L ${labelWidth} -12 L ${labelWidth} 12 L 4 12 Q 0 12 0 8 L 0 -8 Q 0 -12 4 -12 Z`}
                fill={isSelected ? pnlColor : 'rgba(0, 0, 0, .75)'}
                style={{ pointerEvents: 'none' }}
              />

              {/* Border - only left, top, bottom (no right) */}
              <path
                d={`M 4 -12 L ${labelWidth} -12 M 4 12 L ${labelWidth} 12 M 4 -12 Q 0 -12 0 -8 L 0 8 Q 0 12 4 12`}
                fill="none"
                stroke={displayColor}
                strokeWidth={2}
                style={{ pointerEvents: 'none' }}
              />

              {/* Direction arrow */}
              <text
                x={12}
                y={0}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={chartConfig.positionMarkers.fontSize.arrow}
                fontWeight={chartConfig.positionMarkers.fontWeight}
                fill={isSelected ? 'black' : displayColor}
                style={{ pointerEvents: 'none' }}
              >
                {directionArrow}
              </text>

              {/* PnL text */}
              <text
                x={labelWidth / 2 + 8}
                y={0}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={chartConfig.positionMarkers.fontSize.pnl}
                fontWeight={chartConfig.positionMarkers.fontWeight}
                fill={isSelected ? 'black' : pnlColor}
                style={{ pointerEvents: 'none' }}
              >
                {position.totalPnL >= 0 ? '+' : '-'}$
                {formatPrice(Math.abs(position.totalPnL))}
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
}
